import {ErrorBoundary} from 'react-error-boundary';
import React, {Suspense} from 'react';
import {DrivingInfoHeaderProps} from '@di-shared/features/history/driving-info/DrivingInfoHeader/index';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useDrivingInfoEu from '@di-shared/features/history/driving-info/useDrivingInfo.eu';
import {endDateOfMonth, isSameYearAndMonth, startDateOfMonth} from '@shared/dateUtils';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {ROUTE_PATHS} from '@di-shared/routes';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import {getYYYYMMFormat} from '@di-shared/api/di/utils';
import {ERROR} from '@di-shared/const';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';

export default function ({vin, selectedDate, setSelectedDate, changeMonth}: DrivingInfoHeaderProps) {
  return (
    <ErrorBoundary fallback={<FallbackError selectedDate={selectedDate} />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner
          key={getYYYYMMFormat(selectedDate)}
          vin={vin}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          changeMonth={changeMonth}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, selectedDate, setSelectedDate, changeMonth}: DrivingInfoHeaderProps) {
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {t} = useInferableTranslation();
  const {verifiedDrivingDistances} = useDrivingInfoEu({vin});
  const today = new Date();
  const updatedDate = isSameYearAndMonth(today, selectedDate) ? today : endDateOfMonth(selectedDate);

  async function openSelectMonthBottomSheet() {
    const reverseMonths = [...verifiedDrivingDistances.map((driving) => driving.verifiedDate)].reverse();
    const date = await ccWebviewUtils.openBottomSheetWebViewUrl<Date>(ROUTE_PATHS.BOTTOM_SHEET_HISTORY_SELECT_MONTH.getFullPath(), {
      months: reverseMonths,
      selectedDate,
    });

    if (!date) {
      return;
    }

    setSelectedDate(date);
    changeMonth(date);
  }

  return (
    <>
      <div className="section_heading type_main">
        <button
          type="button"
          className="btn_monthly body_medium_b"
          onClick={openSelectMonthBottomSheet}
        >
          {CldrDatetimeFormatter.format(selectedDate, 'yMMM')}
          <span
            className="ic ic_arrow_down_16px"
            aria-hidden="true"
          />
        </button>
        <h3 className="section_heading_title">{t('ctn_mytrips_my_driving_title')}</h3>
        <span
          className="section_heading_caption"
          role="text"
          aria-label={`${t('ctn_mytirps_updated')} ${CldrDatetimeFormatter.format(updatedDate, 'yMMMd')}`}
        >
          {t('ctn_mytirps_updated')} {CldrDatetimeFormatter.format(updatedDate, 'yMMMd')}
        </span>
      </div>
      <div className="section_visual">
        <ImageSelector
          alt="section_visual_img"
          className="section_visual_img"
          kiaLightSrc={() => import('@di-shared/assets/img/visual/my_trips_light.png')}
          kiaDarkSrc={() => import('@di-shared/assets/img/visual/my_trips.png')}
          hyundaiLightSrc={() => import('@di-shared/assets/img/visual/hyundai/my_trips_light.png')}
          hyundaiDarkSrc={() => import('@di-shared/assets/img/visual/hyundai/my_trips.png')}
          genesisLightSrc={() => import('@di-shared/assets/img/visual/genesis/my_trips_light.png')}
          genesisDarkSrc={() => import('@di-shared/assets/img/visual/genesis/my_trips.png')}
        />
      </div>
    </>
  );
}

function FallbackLoading() {
  return (
    <div aria-hidden="true">
      <div className="section_heading type_main">
        <button
          type="button"
          className="btn_monthly body_medium_b skeleton"
          style={{width: '160px', height: '20px'}}
        />
        <h3
          className="section_heading_title skeleton"
          style={{width: '200px', height: '28px'}}
        />
        <span
          className="section_heading_caption skeleton"
          style={{width: '160px', height: '20px'}}
        />
      </div>
      <div className="section_visual skeleton">
        <ImageSelector
          alt="section_visual_img"
          className="section_visual_img"
          kiaLightSrc={() => import('@di-shared/assets/img/visual/my_trips_light.png')}
          kiaDarkSrc={() => import('@di-shared/assets/img/visual/my_trips.png')}
          hyundaiLightSrc={() => import('@di-shared/assets/img/visual/my_trips_light.png')}
          hyundaiDarkSrc={() => import('@di-shared/assets/img/visual/my_trips.png')}
          genesisLightSrc={() => import('@di-shared/assets/img/visual/my_trips_light.png')}
          genesisDarkSrc={() => import('@di-shared/assets/img/visual/my_trips.png')}
        />
      </div>
    </div>
  );
}

function FallbackError({selectedDate}: {selectedDate: Date}) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const startDate = startDateOfMonth(selectedDate);

  return (
    <>
      <div className="section_heading type_main">
        <button
          type="button"
          className="btn_monthly body_medium_b"
        >
          {CldrDatetimeFormatter.format(startDate, 'yMMM')}
          <span
            className="ic ic_arrow_down_16px"
            aria-hidden="true"
          />
        </button>
        <h3 className="section_heading_title">{t('ctn_mytrips_my_driving_title')}</h3>
        <span
          className="section_heading_caption"
          role="text"
          aria-label={`${t('ctn_mytirps_updated')} ${ERROR.DASH}`}
        >
          {t('ctn_mytirps_updated')} {ERROR.DASH}
        </span>
      </div>
      <div className="section_visual">
        <ImageSelector
          alt="section_visual_img"
          className="section_visual_img"
          kiaLightSrc={() => import('@di-shared/assets/img/visual/my_trips_light.png')}
          kiaDarkSrc={() => import('@di-shared/assets/img/visual/my_trips.png')}
          hyundaiLightSrc={() => import('@di-shared/assets/img/visual/my_trips_light.png')}
          hyundaiDarkSrc={() => import('@di-shared/assets/img/visual/my_trips.png')}
          genesisLightSrc={() => import('@di-shared/assets/img/visual/my_trips_light.png')}
          genesisDarkSrc={() => import('@di-shared/assets/img/visual/my_trips.png')}
        />
      </div>
    </>
  );
}
